import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Download,
  BookOpen,
  FileText,
  TrendingUp,
  Home,
  Lock,
  Mail,
  Send,
  Heart,
  PenLine,
  ChevronRight,
  Star,
  Calendar,
  User,
  FolderOpen,
  Link2,
  Video,
  ExternalLink,
  MapPin,
  CheckCircle,
  Clock,
  Loader2,
  Play,
  PlayCircle,
  RefreshCw,
  MessageCircle,
  Sparkles,
  Bell,
  AlertCircle,
  Megaphone
} from './icons/CustomDashboardIcons';
import { 
  StarburstIcon, SpiralIcon, FlameIcon, HeartAlchemyIcon, LotusIcon, 
  CrystalIcon, InfinityAlchemyIcon, CompassIcon, ScrollIcon, SanctuaryIcon
} from './icons/AlchemyIcons';
import { Skeleton } from './ui/skeleton';
import { toast } from 'sonner';
import { PDF_URL, APP_CONFIG } from '../constants/appConfig';
import chaptersData from '../data/chapters.json';
import FeedbackForm from './FeedbackForm';
import MessagingCenter from './MessagingCenter';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import ScholarshipRequestDialog from './ScholarshipRequestDialog';
import PremiumFooter from './PremiumFooter';
import { DashboardBottomDock } from './DashboardBottomDock';
import { DashboardAnnouncementCards } from './DashboardAnnouncementCards';
import EmpathyAlertButton from './EmpathyAlertButton';
import UserMessaging from './UserMessaging';
import { generateElchmEResearchLink } from '../utils/elchmeSSO';
import { generateMairyWisdomLink } from '../utils/mairySSO';
import { useWebSocket, ConnectionState } from '../hooks/useWebSocket';
import { WebSocketStatus } from './WebSocketStatus';
import { DashboardAnnouncements } from './DashboardAnnouncements';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function Dashboard() {
  const [user, setUser] = useState(null);
  const [showScholarshipDialog, setShowScholarshipDialog] = useState(false);
  const [progress, setProgress] = useState([]);
  const [responses, setResponses] = useState([]);
  const [journal, setJournal] = useState([]);
  const [summary, setSummary] = useState('');
  const [stats, setStats] = useState({ completed: 0, total_responses: 0 });
  const [newEntry, setNewEntry] = useState({ title: '', content: '' });
  const [activeTab, setActiveTab] = useState('summary');
  const [blogDraft, setBlogDraft] = useState({ title: '', content: '', excerpt: '' });
  const [resources, setResources] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [selectedHomework, setSelectedHomework] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [videos, setVideos] = useState([]);
  const [latestVideo, setLatestVideo] = useState(null);
  
  // ElchmE Integration state
  const [elchmeActivities, setElchmeActivities] = useState([]);
  const [elchmeStats, setElchmeStats] = useState({ total_sketches: 0, total_soul_scores: 0, total_visions: 0 });
  const [latestSoulScore, setLatestSoulScore] = useState(null);
  
  // m.ai.ry Integration state
  const [mairyConversations, setMairyConversations] = useState([]);
  const [mairyInsights, setMairyInsights] = useState([]);
  const [mairyStats, setMairyStats] = useState({ total_conversations: 0, total_insights: 0 });
  const [mairyLastSync, setMairyLastSync] = useState(null);
  const [mairyLoading, setMairyLoading] = useState(false);
  const [mairyActiveTab, setMairyActiveTab] = useState('insights');
  
  // Notifications and Messaging state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [empathyAlerts, setEmpathyAlerts] = useState([]);
  const [showMessaging, setShowMessaging] = useState(false);
  const [initialConversationId, setInitialConversationId] = useState(null);
  const [launchingElchmE, setLaunchingElchmE] = useState(false);
  const [launchingMairy, setLaunchingMairy] = useState(false);

  const [dataLoading, setDataLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  // WebSocket handlers for real-time updates
  const handleEmpathyAlert = useCallback((alert) => {
    console.log('[Dashboard] Received Empathy Alert:', alert);
    toast.info(
      <div className="flex flex-col gap-1">
        <span className="font-semibold text-rose-400">💗 {alert.title}</span>
        <span className="text-sm">{alert.sender_name} needs empathetic support</span>
      </div>,
      { duration: 10000 }
    );
    setEmpathyAlerts(prev => [alert, ...prev]);
    setUnreadCount(prev => prev + 1); // Increment for empathy alerts too
  }, []);

  const handleNotification = useCallback((notification) => {
    console.log('[Dashboard] Received Notification:', notification);
    toast.info(notification.title || 'New notification');
    setNotifications(prev => [notification, ...prev]);
    setUnreadCount(prev => prev + 1);
  }, []);

  const handleNewMessage = useCallback((data) => {
    console.log('[Dashboard] New message from:', data.sender_name);
    toast.info(
      <div className="flex flex-col gap-1">
        <span className="font-semibold">New message from {data.sender_name}</span>
        <span className="text-sm text-stone-400">{data.preview}</span>
      </div>,
      { duration: 5000 }
    );
    setUnreadCount(prev => prev + 1); // Increment for new messages
  }, []);

  const handleConnectionChange = useCallback((state) => {
    if (state === ConnectionState.CONNECTED) {
      console.log('[Dashboard] WebSocket connected - real-time updates active');
    } else if (state === ConnectionState.DISCONNECTED) {
      console.log('[Dashboard] WebSocket disconnected');
    }
  }, []);

  // Initialize WebSocket connection
  const {
    connectionState,
    isConnected,
    onlineUsers
  } = useWebSocket(
    user ? { _id: user.id, name: user.name } : null,
    {
      autoConnect: true,
      onEmpathyAlert: handleEmpathyAlert,
      onNotification: handleNotification,
      onNewMessage: handleNewMessage,
      onConnectionChange: handleConnectionChange
    }
  );

  useEffect(() => {
    // Check session with a slight delay to ensure localStorage is fully available
    const checkSession = () => {
      const userId = localStorage.getItem('workbook_user_id');
      const userName = localStorage.getItem('workbook_user_name');
      const userEmail = localStorage.getItem('workbook_user_email');
      
      if (!userId || !userEmail) {
        // Give a brief moment for any async session restoration to complete
        setTimeout(() => {
          const retryUserId = localStorage.getItem('workbook_user_id');
          const retryUserEmail = localStorage.getItem('workbook_user_email');
          if (!retryUserId || !retryUserEmail) {
            window.location.href = '/login';
          } else {
            setUser({ 
              id: retryUserId, 
              name: localStorage.getItem('workbook_user_name'), 
              email: retryUserEmail 
            });
            initializeData(retryUserId);
          }
        }, 100);
        return;
      }
      
      setUser({ id: userId, name: userName, email: userEmail });
      initializeData(userId);
    };
    
    // Initial data load with retry mechanism
    const initializeData = async (userId) => {
      setDataLoading(true);
      await loadUserData(userId);
      await fetchAnnouncements();
      setDataLoading(false);
    };
    
    checkSession();
    
    // Fetch notifications and empathy alerts
    const fetchNotificationsAndAlerts = async (userId) => {
      try {
        // Fetch notifications
        const notifResponse = await fetch(`${BACKEND_URL}/api/notifications`, {
          headers: { 'X-User-Id': userId }
        });
        if (notifResponse.ok) {
          const notifData = await notifResponse.json();
          setNotifications(notifData.notifications || []);
          setUnreadCount(notifData.unread_count || 0);
        }
        
        // Fetch active empathy alerts (ones the user can respond to)
        const alertsResponse = await fetch(`${BACKEND_URL}/api/empathy-alerts?status=active`, {
          headers: { 'X-User-Id': userId }
        });
        if (alertsResponse.ok) {
          const alertsData = await alertsResponse.json();
          setEmpathyAlerts(alertsData.alerts || []);
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    
    // Initial notification fetch
    const userId = localStorage.getItem('workbook_user_id');
    if (userId) {
      fetchNotificationsAndAlerts(userId);
    }
    
    // Poll for notifications every 10 seconds
    const notifInterval = setInterval(() => {
      const currentUserId = localStorage.getItem('workbook_user_id');
      if (currentUserId) {
        fetchNotificationsAndAlerts(currentUserId);
      }
    }, 10000);
    
    // Auto-refresh data every 60 seconds to keep it current
    const refreshInterval = setInterval(() => {
      const currentUserId = localStorage.getItem('workbook_user_id');
      if (currentUserId) {
        loadUserData(currentUserId);
      }
    }, 60000);
    
    return () => {
      clearInterval(refreshInterval);
      clearInterval(notifInterval);
    };
  }, []);

  // Retry data load if initial load failed
  useEffect(() => {
    if (!dataLoading && retryCount < 3 && user) {
      const hasData = progress.length > 0 || resources.length > 0 || highlights.length > 0;
      if (!hasData) {
        const retryTimeout = setTimeout(() => {
          console.log(`Retrying data load (attempt ${retryCount + 1})...`);
          loadUserData(user.id);
          fetchAnnouncements();
          setRetryCount(prev => prev + 1);
        }, 1500); // Wait 1.5 seconds before retry
        return () => clearTimeout(retryTimeout);
      }
    }
  }, [dataLoading, retryCount, user, progress.length, resources.length, highlights.length]);

  // Fetch m.ai.ry data when user is loaded
  useEffect(() => {
    if (user && user.id) {
      fetchMairyData();
    }
  }, [user]);

  const loadUserData = async (userId) => {
    try {
      // Use Promise.allSettled to fetch all data in parallel and handle partial failures
      const results = await Promise.allSettled([
        fetch(`${BACKEND_URL}/api/workbook/progress/${userId}`),
        fetch(`${BACKEND_URL}/api/workbook/responses/${userId}`),
        fetch(`${BACKEND_URL}/api/workbook/journal/${userId}`),
        fetch(`${BACKEND_URL}/api/workbook/summary/${userId}`),
        fetch(`${BACKEND_URL}/api/student/materials`),
        fetch(`${BACKEND_URL}/api/ebook/highlights/all`),
        fetch(`${BACKEND_URL}/api/videos`),
        fetch(`${BACKEND_URL}/api/videos/latest`),
        fetch(`${BACKEND_URL}/api/elchme/my-activities`, { headers: { 'X-User-Id': userId } }),
        fetch(`${BACKEND_URL}/api/elchme/soul-scores`, { headers: { 'X-User-Id': userId } })
      ]);

      // Process progress
      if (results[0].status === 'fulfilled' && results[0].value.ok) {
        const data = await results[0].value.json();
        setProgress(data.progress || []);
      }

      // Process responses
      if (results[1].status === 'fulfilled' && results[1].value.ok) {
        const data = await results[1].value.json();
        setResponses(data.responses || []);
      }

      // Process journal
      if (results[2].status === 'fulfilled' && results[2].value.ok) {
        const data = await results[2].value.json();
        setJournal(data.entries || []);
      }

      // Process summary
      if (results[3].status === 'fulfilled' && results[3].value.ok) {
        const data = await results[3].value.json();
        setSummary(data.summary || '');
        setStats(data.stats || { completed: 0 });
      }

      // Process materials/resources
      if (results[4].status === 'fulfilled' && results[4].value.ok) {
        const data = await results[4].value.json();
        setResources(data.materials || []);
      }

      // Process highlights
      if (results[5].status === 'fulfilled' && results[5].value.ok) {
        const data = await results[5].value.json();
        setHighlights(data.filter(h => h.active) || []);
      }

      // Process videos
      if (results[6].status === 'fulfilled' && results[6].value.ok) {
        const data = await results[6].value.json();
        setVideos(data || []);
      }

      // Process latest video
      if (results[7].status === 'fulfilled' && results[7].value.ok) {
        const data = await results[7].value.json();
        setLatestVideo(data);
      }

      // Process ElchmE activities
      if (results[8].status === 'fulfilled' && results[8].value.ok) {
        const data = await results[8].value.json();
        setElchmeActivities(data.activities || []);
        setElchmeStats(data.stats || {});
      }

      // Process Soul Scores
      if (results[9].status === 'fulfilled' && results[9].value.ok) {
        const data = await results[9].value.json();
        setLatestSoulScore(data.latest_score);
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/announcements`);
      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data.announcements || []);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };


  // Launch ElchmE.com with SSO (ElchmE's secure SHA-256 token system)
  const launchElchmE = async () => {
    if (!user) {
      toast.error('Please log in to access ElchmE.com');
      return;
    }

    setLaunchingElchmE(true);

    try {
      // Generate secure SSO link using ElchmE's system
      const elchmeUrl = await generateElchmEResearchLink(user);
      
      toast.success('Opening ElchmE.com...');
      window.open(elchmeUrl, '_blank', 'noopener,noreferrer');

    } catch (error) {
      console.error('Failed to launch ElchmE:', error);
      toast.error('Failed to generate secure link. Please try again.');
    } finally {
      setLaunchingElchmE(false);
    }
  };

  const addJournalEntry = async () => {
    if (!newEntry.title || !newEntry.content) {
      toast.error('Please fill in both title and content');
      return;
    }

    if (!user || !user._id) {
      toast.error('Please log in to save journal entries');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workbook/journal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user._id,
          title: newEntry.title,
          content: newEntry.content,
          date: new Date().toISOString()
        })
      });

      if (response.ok) {
        toast.success('Journal entry saved!');
        setNewEntry({ title: '', content: '' });

        // Reload journal data
        if (user._id) {
          const journalRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workbook/journal/${user._id}`);
          if (journalRes.ok) {
            const data = await journalRes.json();
            setJournal(data.entries || []);
          }
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to save journal entry');
      }
    } catch (error) {
      console.error('Journal save error:', error);
      toast.error('Failed to add journal entry');
    }
  };

  // Launch m.ai.ry Wisdom Bot
  const launchMairy = async () => {
    if (!user) {
      toast.error('Please log in to access m.ai.ry');
      return;
    }

    setLaunchingMairy(true);

    try {
      // Generate secure SSO link for m.ai.ry
      const mairyUrl = await generateMairyWisdomLink(user, {
        from: 'dashboard',
        seeking: 'wisdom'
      });
      
      toast.success('Opening m.ai.ry Wisdom Bot...');
      window.open(mairyUrl, '_blank', 'noopener,noreferrer');

    } catch (error) {
      console.error('Failed to launch m.ai.ry:', error);
      toast.info('Opening m.ai.ry...');
      window.open('https://mairy.io', '_blank', 'noopener,noreferrer');
    } finally {
      setLaunchingMairy(false);
    }
  };

  // Fetch ElchmE activities
  const fetchElchmEData = async () => {
    if (!user) return;
    
    try {
      // Fetch activities
      const activitiesRes = await fetch(`${BACKEND_URL}/api/elchme/my-activities`, {
        headers: { 'X-User-Id': user._id }
      });
      
      if (activitiesRes.ok) {
        const data = await activitiesRes.json();
        setElchmeActivities(data.activities || []);
        setElchmeStats(data.stats || {});
      }
      
      // Fetch latest soul score
      const scoresRes = await fetch(`${BACKEND_URL}/api/elchme/soul-scores`, {
        headers: { 'X-User-Id': user._id }
      });
      
      if (scoresRes.ok) {
        const data = await scoresRes.json();
        setLatestSoulScore(data.latest_score);
      }
    } catch (error) {
      console.error('Failed to fetch ElchmE data:', error);
    }
  };

  // Fetch m.ai.ry synced data
  const fetchMairyData = async () => {
    if (!user) return;
    
    setMairyLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/mairy/data/${user.id}`);
      
      if (response.ok) {
        const data = await response.json();
        setMairyConversations(data.conversations || []);
        setMairyInsights(data.insights || []);
        setMairyStats(data.stats || { total_conversations: 0, total_insights: 0 });
        setMairyLastSync(data.last_sync);
      }
    } catch (error) {
      console.error('Failed to fetch m.ai.ry data:', error);
    } finally {
      setMairyLoading(false);
    }
  };

  const downloadPDF = () => {
    const link = document.createElement('a');
    link.href = PDF_URL;
    link.download = APP_CONFIG.workbookPdfFilename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Belonging eBook PDF download started');
  };

  const submitBlogPost = async () => {
    if (!blogDraft.title || !blogDraft.content) {
      toast.error('Please fill in title and content');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/blog-submission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          user_name: user.name,
          user_email: user.email,
          title: blogDraft.title,
          content: blogDraft.content,
          excerpt: blogDraft.excerpt || blogDraft.content.substring(0, 150),
          status: 'pending_review'
        })
      });

      if (!response.ok) throw new Error('Failed');

      toast.success('Your story has been submitted for review!');
      setBlogDraft({ title: '', content: '', excerpt: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    }
  };

  // Loading skeleton for resources
  const ResourceSkeleton = () => (
    <div className="space-y-4">
      {[1, 2, 3].map(i => (
        <div key={i} className="bg-stone-900/50 border border-stone-800 rounded-lg p-6 animate-pulse">
          <div className="flex items-start gap-4">
            <Skeleton className="w-12 h-12 rounded-lg bg-stone-800" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-24 bg-stone-800" />
              <Skeleton className="h-5 w-3/4 bg-stone-800" />
              <Skeleton className="h-3 w-1/2 bg-stone-800" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Full page loading state
  if (!user) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-4" />
        <div className="text-amber-500">Loading your dashboard...</div>
      </div>
    </div>
  );

  const totalExercises = chaptersData.chapters.reduce((sum, ch) => sum + ch.exercises.length, 0);
  const overallProgress = totalExercises > 0 ? (stats.completed / totalExercises) * 100 : 0;

  // Filter for Zoom link
  const zoomAnnouncement = announcements.find(a => a.zoom_link) || announcements[0];
  const homeworkAssignments = resources.filter(r => r.material_type === 'homework');

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="relative overflow-hidden border-b border-amber-500/20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(212, 175, 55, 0.4) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
          {/* Scholarship CTA moved to bottom dock */}
          {/* <div className="mb-6 flex items-center justify-between bg-gradient-to-r from-amber-900/30 to-transparent border border-amber-500/30 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <StarburstIcon className="w-5 h-5 text-amber-500" />
              <span className="text-sm text-amber-200">
                Need financial assistance? Request a scholarship to access premium features.
              </span>
            </div>
            <Button
              onClick={() => setShowScholarshipDialog(true)}
              className="bg-amber-600 hover:bg-amber-500 text-black font-semibold rounded-none"
              size="sm"
            >
              Request Scholarship
            </Button>
          </div> */}

          <div className="flex justify-between items-center">
            <div>
              <p className="text-amber-500 text-xs tracking-[0.3em] uppercase mb-2">Welcome back</p>
              <h1 className="text-4xl font-light tracking-wide" style={{ fontFamily: 'Playfair Display, serif' }}>
                {user.name}
              </h1>
              <p className="text-stone-500 text-sm mt-1">{user.email}</p>
            </div>
            <div className="flex gap-3 items-center">
              {/* WebSocket Status Indicator */}
              <WebSocketStatus connectionState={connectionState} onlineCount={onlineUsers.length} />
              
              {/* Live Notification Badge */}
              {unreadCount > 0 && (
                <div className="relative animate-pulse" title={`${unreadCount} unread notifications`} data-testid="unread-badge">
                  <Bell className="w-5 h-5 text-rose-400" />
                  <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold bg-rose-500 text-white rounded-full px-1">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                </div>
              )}
              
              {/* Loading indicator */}
              {dataLoading && (
                <div className="flex items-center gap-2 text-amber-500/70 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="hidden md:inline">Loading...</span>
                </div>
              )}
              {/* Refresh button with pulse animation */}
              <Button 
                onClick={() => { setDataLoading(true); loadUserData(user.id); fetchAnnouncements(); }}
                variant="ghost"
                className="text-stone-500 hover:text-amber-500 rounded-none px-2 group"
                title="Refresh data"
                data-testid="refresh-dashboard-btn"
              >
                <RefreshCw className={`w-4 h-4 transition-transform ${dataLoading ? 'animate-spin' : 'group-hover:rotate-180'} ${!dataLoading ? 'animate-pulse-subtle' : ''}`} />
              </Button>
              {/* Sessions button with pulse */}
              <button 
                onClick={() => {
                  setActiveTab('videos');
                  requestAnimationFrame(() => {
                    const el = document.getElementById('session-recordings');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  });
                }}
                className="flex items-center gap-2 px-3 py-2 bg-transparent border border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/60 transition-colors animate-pulse-subtle"
                title="Session Recordings"
                data-testid="sessions-btn"
              >
                <PlayCircle className="w-4 h-4" />
                <span className="hidden md:inline text-sm">Sessions</span>
              </button>
              {/* ElchmE.com link - Direct to main site */}
              <Button 
                onClick={() => window.open('https://elchme.com', '_blank', 'noopener,noreferrer')}
                className="bg-transparent border border-amber-500/30 text-amber-500 hover:bg-amber-500/10 hover:border-amber-500/60 rounded-none px-3 animate-pulse-subtle" 
                title="Launch ElchmE.com" 
                data-testid="elchme-btn"
              >
                <InfinityAlchemyIcon className="w-4 h-4 mr-1" />
                <span className="font-light tracking-wide">
                  <span className="text-blue-400">E</span>
                  <span className="text-red-400">l</span>
                  <span className="text-emerald-400">c</span>
                  <span className="text-amber-400">h</span>
                  <span className="text-blue-400">m</span>
                  <span className="text-red-400">E</span>
                </span>
              </Button>

              {/* m.ai.ry Wisdom Bot button with SSO */}
              <Button 
                onClick={launchMairy}
                disabled={launchingMairy}
                className="bg-transparent border border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/60 rounded-none px-3 animate-pulse-subtle" 
                title="Chat with m.ai.ry Wisdom Bot" 
                data-testid="mairy-btn"
              >
                {launchingMairy ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <LotusIcon className="w-4 h-4 mr-1" />
                )}
                <span className="font-light tracking-wide bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                  m.ai.ry
                </span>
              </Button>
              {/* Forum link */}
              <a href="/forum">
                <Button className="bg-transparent border border-teal-500/30 text-teal-400 hover:bg-teal-500/10 hover:border-teal-500/60 rounded-none px-3" title="Community Forum" data-testid="forum-btn">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Forum
                </Button>
              </a>
              <a href={PDF_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-amber-600 hover:bg-amber-500 text-black font-semibold rounded-none">
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </Button>
              </a>
              <a href="/belonging-ebook">
                <Button className="bg-transparent border border-amber-500/50 text-amber-500 hover:bg-amber-500/20 rounded-none">
                  <BookOpen className="w-4 h-4 mr-2" />
                  eBook
                </Button>
              </a>
              <a href="/lore">
                <Button className="bg-transparent border border-amber-500/50 text-amber-500 hover:bg-amber-500/20 rounded-none">
                  <FileText className="w-4 h-4 mr-2" />
                  LORE
                </Button>
              </a>
              <Button 
                onClick={() => { localStorage.clear(); window.location.href = '/'; }}
                variant="ghost"
                className="text-red-500/70 hover:text-red-400 rounded-none"
              >
                Logout
              </Button>
            </div>
          </div>

          {/* Quick Stats Row intentionally removed (duplicate of Stats & Progress section below) */}
        </div>
      </header>

      {/* Announcements (single source) */}
      <section className="border-b border-sky-500/30 bg-gradient-to-r from-sky-200/90 via-sky-100/90 to-sky-200/90">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {zoomAnnouncement && zoomAnnouncement.zoom_link ? (
            <>
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-sky-600 flex items-center justify-center shadow-lg shadow-sky-500/30">
                    <LotusIcon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-1">Announcements</h2>
                    <p className="text-slate-700 text-lg">{zoomAnnouncement.title || 'Weekly Gathering'}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                      <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Thursdays</span>
                      <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> 12:00 PM CT</span>
                    </div>
                  </div>
                </div>
                <a 
                  href={zoomAnnouncement.zoom_link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size="lg" className="bg-sky-600 hover:bg-sky-500 text-white font-semibold px-4 py-3 rounded-xl shadow-lg shadow-sky-500/20 transition-transform hover:scale-[1.02]">
                    Join Zoom Meeting
                    <ExternalLink className="ml-2 w-4 h-4" />
                  </Button>
                </a>
              </div>
              
              {/* Additional announcements (up to 2 more) */}
              <DashboardAnnouncementCards announcements={announcements} />
            </>
          ) : (
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-stone-800 flex items-center justify-center border border-stone-700">
                  <Calendar className="w-8 h-8 text-stone-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">Next Session</h2>
                  <p className="text-stone-400 text-lg">Schedule coming soon...</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* THIS WEEK'S HIGHLIGHTS - TOP PRIORITY PLACEMENT */}
      {highlights.length > 0 && (
        <section className="py-8 px-6 border-b border-emerald-500/20 bg-gradient-to-r from-emerald-950/30 via-stone-950 to-emerald-950/30" data-testid="highlights-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl font-light text-emerald-100" style={{ fontFamily: 'Playfair Display, serif' }}>
                    This Week's Highlights
                  </h2>
                  <p className="text-xs text-emerald-500/60">Featured pages for your practice</p>
                </div>
              </div>
              <a href="/belonging-ebook" className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
                View All <ChevronRight className="w-3 h-3" />
              </a>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {highlights.map((highlight) => (
                <a
                  key={highlight._id}
                  href={`/belonging-ebook?page=${highlight.page}`}
                  className="group flex items-center gap-4 bg-stone-900/80 border border-emerald-500/20 hover:border-emerald-500/50 rounded-xl p-4 transition-all hover:bg-stone-800/80 hover:shadow-lg hover:shadow-emerald-500/10"
                  data-testid={`highlight-${highlight.page}`}
                >
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-emerald-900/40 to-emerald-800/20 border border-emerald-500/30 rounded-lg flex items-center justify-center text-emerald-400 font-serif font-semibold text-xl group-hover:scale-105 transition-transform shadow-inner">
                    {highlight.page}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-emerald-400/70 text-[10px] tracking-wider uppercase mb-1 font-medium">
                      {highlight.session_name || 'Featured Page'}
                    </p>
                    <h3 className="text-white font-medium text-sm truncate group-hover:text-emerald-300 transition-colors">
                      {highlight.title}
                    </h3>
                  </div>
                  <ChevronRight className="w-5 h-5 text-stone-600 group-hover:text-emerald-400 transition-colors" />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sacred Ecosystem moved to footer bottom dock */}

      {/* Notifications & Empathy Alerts Section */}
      {(notifications.length > 0 || empathyAlerts.length > 0) && (
        <section className="bg-gradient-to-r from-rose-900/20 to-amber-900/20 border-b border-rose-500/20">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-rose-400" />
                <h3 className="text-lg font-medium text-white">
                  Notifications
                  {unreadCount > 0 && (
                    <Badge className="ml-2 bg-rose-500 text-white">{unreadCount}</Badge>
                  )}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setShowMessaging(true)}
                  variant="ghost"
                  className="text-amber-400 hover:text-amber-300"
                  data-testid="open-messaging-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-1" />
                  Messages
                </Button>
                <EmpathyAlertButton variant="compact" />
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* Notifications */}
              {notifications.slice(0, 3).map(notif => (
                <div 
                  key={notif._id}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    notif.read 
                      ? 'bg-stone-800/30 border-stone-700 hover:border-stone-600' 
                      : 'bg-rose-500/10 border-rose-500/30 hover:border-rose-500/50'
                  }`}
                  onClick={() => {
                    if (notif.conversation_id) {
                      setInitialConversationId(notif.conversation_id);
                      setShowMessaging(true);
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      notif.type === 'empathy_alert' ? 'bg-rose-500/20' :
                      notif.type === 'empathy_response' ? 'bg-teal-500/20' : 'bg-amber-500/20'
                    }`}>
                      {notif.type === 'empathy_alert' ? (
                        <HeartAlchemyIcon className="w-4 h-4 text-rose-400" />
                      ) : notif.type === 'empathy_response' ? (
                        <Heart className="w-4 h-4 text-teal-400" />
                      ) : (
                        <MessageCircle className="w-4 h-4 text-amber-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{notif.title}</p>
                      <p className="text-xs text-stone-400 mt-1">{notif.message}</p>
                    </div>
                    {!notif.read && (
                      <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                    )}
                  </div>
                </div>
              ))}
              
              {/* Active Empathy Alerts from Others */}
              {empathyAlerts.slice(0, 2).map(alert => (
                <div 
                  key={alert._id}
                  className="p-4 rounded-lg bg-gradient-to-r from-rose-500/10 to-amber-500/10 border border-rose-500/30 hover:border-rose-500/50 cursor-pointer transition-all"
                  onClick={async () => {
                    // Respond to empathy alert
                    try {
                      const response = await fetch(`${BACKEND_URL}/api/empathy-alerts/${alert._id}/respond`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'X-User-Id': user?.id,
                          'X-User-Name': user?.name
                        },
                        body: JSON.stringify({ alert_id: alert._id, message: "I'm here to help" })
                      });
                      if (response.ok) {
                        const data = await response.json();
                        toast.success('You offered to help! A conversation has been started.');
                        setInitialConversationId(data.conversation_id);
                        setShowMessaging(true);
                        // Remove from list
                        setEmpathyAlerts(prev => prev.filter(a => a._id !== alert._id));
                      }
                    } catch (error) {
                      toast.error('Failed to respond to alert');
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-rose-500/30 to-amber-500/30 flex items-center justify-center">
                      <HeartAlchemyIcon className="w-5 h-5 text-rose-400 animate-pulse" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-rose-300">🆘 Empathy Request</p>
                        <Badge className={`text-xs ${
                          alert.urgency === 'urgent' ? 'bg-red-500' :
                          alert.urgency === 'high' ? 'bg-orange-500' :
                          alert.urgency === 'normal' ? 'bg-amber-500' : 'bg-blue-500'
                        } text-white`}>
                          {alert.urgency}
                        </Badge>
                      </div>
                      <p className="text-white mt-1">{alert.title}</p>
                      <p className="text-xs text-stone-400 mt-1">
                        From {alert.created_by_name} · Click to offer support
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-stone-500" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* ElchmE Activity Integration */}
      {user && elchmeActivities.length > 0 && (
        <section className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 border-b border-purple-500/20">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-indigo-500/20 flex items-center justify-center">
                  <InfinityAlchemyIcon className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                    ElchmE Activity
                  </h2>
                  <p className="text-sm text-purple-300">
                    Your creative work from ElchmE.com
                  </p>
                </div>
              </div>
              
              {/* Stats */}
              <div className="flex gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-400">{elchmeStats.total_sketches || 0}</div>
                  <div className="text-xs text-stone-400">Sketches</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-400">{elchmeStats.total_soul_scores || 0}</div>
                  <div className="text-xs text-stone-400">Soul Scores</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-400">{elchmeStats.total_visions || 0}</div>
                  <div className="text-xs text-stone-400">Visions</div>
                </div>
              </div>
            </div>

            {/* Latest Soul Score */}
            {latestSoulScore && (
              <Card className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border-purple-500/30 mb-4">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-purple-300">Latest Soul Score</h3>
                      <p className="text-sm text-stone-400">From ElchmE Research Portal</p>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-purple-400">{latestSoulScore.score}</div>
                      <div className="text-xs text-stone-400">{new Date(latestSoulScore.date).toLocaleDateString()}</div>
                    </div>
                  </div>
                  {latestSoulScore.notes && (
                    <p className="mt-4 text-sm text-stone-300 italic">{latestSoulScore.notes}</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Recent Activities */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {elchmeActivities.slice(0, 6).map(activity => (
                <Card key={activity.id} className="bg-stone-900/50 border-stone-700 hover:border-purple-500/50 transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        activity.type === 'sketch' ? 'bg-purple-500/20 text-purple-400' :
                        activity.type === 'soul_score' ? 'bg-indigo-500/20 text-indigo-400' :
                        activity.type === 'vision_quest' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-stone-700 text-stone-400'
                      }`}>
                        {activity.type === 'sketch' ? '🎨' :
                         activity.type === 'soul_score' ? '📊' :
                         activity.type === 'vision_quest' ? '✨' : '📝'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="text-xs bg-purple-500/20 text-purple-300 border-purple-500/30">
                            {activity.type.replace('_', ' ')}
                          </Badge>
                        </div>
                        <h4 className="font-medium text-white text-sm truncate">{activity.title}</h4>
                        <p className="text-xs text-stone-400 mt-1">
                          {new Date(activity.completed_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* View All Button */}
            {elchmeActivities.length > 6 && (
              <div className="mt-4 text-center">
                <Button 
                  variant="ghost" 
                  className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                  onClick={() => setActiveTab('elchme')}
                >
                  View All ElchmE Activity
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        </section>
      )}


      {/* m.ai.ry Wisdom Integration */}
      {user && (mairyConversations.length > 0 || mairyInsights.length > 0 || mairyLoading) && (
        <section className="bg-gradient-to-r from-pink-900/20 to-purple-900/20 border-b border-pink-500/20" data-testid="mairy-wisdom-section">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center">
                  <LotusIcon className="w-6 h-6 text-pink-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                    <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                      m.ai.ry
                    </span>{' '}Wisdom
                  </h2>
                  <p className="text-sm text-pink-300">
                    Your conversations and insights from m.ai.ry
                    {mairyLastSync && (
                      <span className="ml-2 text-stone-500">
                        · Last sync: {new Date(mairyLastSync).toLocaleDateString()}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              
              {/* Stats */}
              <div className="flex gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-pink-400" data-testid="mairy-insights-count">
                    {mairyStats.total_insights || 0}
                  </div>
                  <div className="text-xs text-stone-400">Wisdom Docs</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-400" data-testid="mairy-conversations-count">
                    {mairyStats.total_conversations || 0}
                  </div>
                  <div className="text-xs text-stone-400">Chat Sessions</div>
                </div>
                <Button
                  onClick={fetchMairyData}
                  variant="ghost"
                  size="sm"
                  className="text-pink-400 hover:text-pink-300"
                  disabled={mairyLoading}
                  data-testid="mairy-refresh-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${mairyLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {/* Loading State */}
            {mairyLoading && mairyConversations.length === 0 && mairyInsights.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-pink-400 animate-spin mr-2" />
                <span className="text-stone-400">Loading your wisdom...</span>
              </div>
            )}

            {/* Tabs for Insights and Conversations */}
            {(mairyConversations.length > 0 || mairyInsights.length > 0) && (
              <div>
                {/* Tab Navigation */}
                <div className="flex gap-2 mb-4 border-b border-pink-500/20 pb-2">
                  <button
                    onClick={() => setMairyActiveTab('insights')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-all ${
                      mairyActiveTab === 'insights'
                        ? 'bg-pink-500/20 text-pink-300 border-b-2 border-pink-500'
                        : 'text-stone-400 hover:text-stone-300'
                    }`}
                    data-testid="mairy-insights-tab"
                  >
                    <Sparkles className="w-4 h-4" />
                    Wisdom Documents ({mairyInsights.length})
                  </button>
                  <button
                    onClick={() => setMairyActiveTab('conversations')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-all ${
                      mairyActiveTab === 'conversations'
                        ? 'bg-purple-500/20 text-purple-300 border-b-2 border-purple-500'
                        : 'text-stone-400 hover:text-stone-300'
                    }`}
                    data-testid="mairy-conversations-tab"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Chat Sessions ({mairyConversations.length})
                  </button>
                </div>

                {/* Wisdom Documents Tab Content */}
                {mairyActiveTab === 'insights' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="mairy-insights-list">
                    {mairyInsights.length === 0 ? (
                      <div className="col-span-2 text-center py-8">
                        <Sparkles className="w-8 h-8 text-pink-500/30 mx-auto mb-2" />
                        <p className="text-stone-500">No wisdom documents synced yet.</p>
                        <p className="text-stone-600 text-sm mt-1">
                          Chat with m.ai.ry to receive personalized insights.
                        </p>
                      </div>
                    ) : (
                      mairyInsights.slice(0, 6).map(insight => (
                        <Card key={insight.id} className="bg-stone-900/50 border-stone-700 hover:border-pink-500/50 transition-all">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                insight.insight_type === 'wisdom' ? 'bg-pink-500/20 text-pink-400' :
                                insight.insight_type === 'pattern' ? 'bg-purple-500/20 text-purple-400' :
                                'bg-amber-500/20 text-amber-400'
                              }`}>
                                {insight.insight_type === 'wisdom' ? '✨' :
                                 insight.insight_type === 'pattern' ? '🔮' : '💡'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge className="text-xs bg-pink-500/20 text-pink-300 border-pink-500/30">
                                    {insight.insight_type || 'wisdom'}
                                  </Badge>
                                  {insight.created_at && (
                                    <span className="text-xs text-stone-500">
                                      {new Date(insight.created_at).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-stone-300 line-clamp-3">
                                  {insight.content}
                                </p>
                                {insight.context && (
                                  <p className="text-xs text-stone-500 mt-2 italic">
                                    Context: {insight.context}
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                )}

                {/* Chat Sessions Tab Content */}
                {mairyActiveTab === 'conversations' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mairy-conversations-list">
                    {mairyConversations.length === 0 ? (
                      <div className="col-span-3 text-center py-8">
                        <MessageCircle className="w-8 h-8 text-purple-500/30 mx-auto mb-2" />
                        <p className="text-stone-500">No chat sessions synced yet.</p>
                        <p className="text-stone-600 text-sm mt-1">
                          Start a conversation with m.ai.ry to see your sessions here.
                        </p>
                      </div>
                    ) : (
                      mairyConversations.slice(0, 6).map(conv => (
                        <Card key={conv.id} className="bg-stone-900/50 border-stone-700 hover:border-purple-500/50 transition-all">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                conv.conversation_type === 'wisdom_chat' ? 'bg-purple-500/20 text-purple-400' :
                                conv.conversation_type === 'reflection' ? 'bg-pink-500/20 text-pink-400' :
                                'bg-teal-500/20 text-teal-400'
                              }`}>
                                {conv.conversation_type === 'wisdom_chat' ? '💬' :
                                 conv.conversation_type === 'reflection' ? '🪞' : '🌟'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge className="text-xs bg-purple-500/20 text-purple-300 border-purple-500/30">
                                    {(conv.conversation_type || 'chat').replace('_', ' ')}
                                  </Badge>
                                </div>
                                <p className="text-sm text-stone-300 line-clamp-2">
                                  {conv.summary || 'Chat session'}
                                </p>
                                <div className="flex items-center gap-2 mt-2 text-xs text-stone-500">
                                  {conv.messages && conv.messages.length > 0 && (
                                    <span>{conv.messages.length} messages</span>
                                  )}
                                  {conv.created_at && (
                                    <span>· {new Date(conv.created_at).toLocaleDateString()}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                )}

                {/* View More / Launch m.ai.ry */}
                <div className="mt-6 flex items-center justify-center gap-4">
                  {(mairyInsights.length > 6 || mairyConversations.length > 6) && (
                    <Button 
                      variant="ghost" 
                      className="text-pink-400 hover:text-pink-300 hover:bg-pink-500/10"
                    >
                      View All
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                  <Button
                    onClick={launchMairy}
                    disabled={launchingMairy}
                    className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white"
                    data-testid="mairy-launch-btn"
                  >
                    {launchingMairy ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <LotusIcon className="w-4 h-4 mr-2" />
                    )}
                    Chat with m.ai.ry
                  </Button>
                </div>
              </div>
            )}

            {/* Empty State when no data and not loading */}
            {!mairyLoading && mairyConversations.length === 0 && mairyInsights.length === 0 && (
              <div className="text-center py-12" data-testid="mairy-empty-state">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center mx-auto mb-4">
                  <LotusIcon className="w-8 h-8 text-pink-400" />
                </div>
                <h3 className="text-lg font-medium text-white mb-2">
                  Start Your Wisdom Journey
                </h3>
                <p className="text-stone-400 mb-6 max-w-md mx-auto">
                  Chat with m.ai.ry to receive personalized wisdom and insights. 
                  Your conversations will be synced here automatically.
                </p>
                <Button
                  onClick={launchMairy}
                  disabled={launchingMairy}
                  className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white"
                  data-testid="mairy-start-btn"
                >
                  {launchingMairy ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <LotusIcon className="w-4 h-4 mr-2" />
                  )}
                  Start Chatting with m.ai.ry
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 1.5 FEATURED SESSION VIDEO - Spotlighted under announcements */}
      {latestVideo && (
        <section className="relative overflow-hidden border-b border-amber-500/20 bg-gradient-to-b from-stone-950 via-black to-stone-950" data-testid="featured-video-section">
          {/* Theatrical background effects */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-900/10 via-transparent to-amber-900/10"></div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-amber-500/5 rounded-full blur-[100px]"></div>
            <div className="absolute bottom-0 left-1/4 w-[300px] h-[200px] bg-purple-500/5 rounded-full blur-[80px]"></div>
            <div className="absolute bottom-0 right-1/4 w-[300px] h-[200px] bg-teal-500/5 rounded-full blur-[80px]"></div>
          </div>
          
          <div className="relative z-10 max-w-6xl mx-auto px-6 py-12">
            {/* Epic Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full mb-4 animate-pulse-subtle">
                <PlayCircle className="w-4 h-4 text-amber-400" />
                <span className="text-amber-300 text-xs tracking-wider uppercase font-medium">Latest Session Recording</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-light text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                {latestVideo.title}
              </h2>
              {latestVideo.description && (
                <p className="text-stone-400 text-lg max-w-2xl mx-auto">{latestVideo.description}</p>
              )}
            </div>
            
            {/* Video Container with Theatrical Frame */}
            <div className="relative max-w-4xl mx-auto">
              {/* Glow effect behind video */}
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/20 via-purple-500/10 to-teal-500/20 rounded-2xl blur-xl opacity-50 animate-glow-gentle"></div>
              
              {/* Video Frame */}
              <div className="relative bg-stone-900 rounded-xl overflow-hidden border border-amber-500/30 shadow-2xl shadow-amber-900/20">
                <div className="aspect-video">
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${latestVideo.youtube_id}`}
                    title={latestVideo.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              </div>
              
              {/* Video Meta */}
              <div className="flex items-center justify-center gap-6 mt-6 text-stone-400 text-sm">
                {latestVideo.session_date && (
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-amber-500/70" />
                    {new Date(latestVideo.session_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </span>
                )}
                {latestVideo.duration_minutes && (
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-500/70" />
                    {latestVideo.duration_minutes} minutes
                  </span>
                )}
                {latestVideo.session_number && (
                  <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs font-medium border border-purple-500/30">
                    Session {latestVideo.session_number}
                  </span>
                )}
              </div>
              
              {/* CTA */}
              <div className="flex justify-center mt-6">
                <button 
                  onClick={() => {
                    setActiveTab('videos');
                    // Scroll to the recordings section (instead of a separate page)
                    requestAnimationFrame(() => {
                      const el = document.getElementById('session-recordings');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    });
                  }}
                  className="flex items-center gap-2 px-6 py-3 bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 hover:border-amber-500/50 rounded-lg transition-all group"
                  data-testid="view-all-sessions-btn"
                >
                  <span>View All Session Recordings</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 2. Current Assignments (PRIORITY #2) */}
      {homeworkAssignments.length > 0 && (
        <section className="py-10 px-6 border-b border-stone-800 bg-stone-900/30">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-orange-500/20 text-orange-400">
                <BookOpen className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Current Assignments
                </h2>
                <p className="text-stone-500 text-sm">Required homework for this week</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {homeworkAssignments.map(resource => (
                <button
                  key={resource._id}
                  onClick={() => setSelectedHomework(resource)}
                  className="group text-left bg-stone-900 border border-stone-800 rounded-xl p-6 hover:border-orange-500/50 transition-all hover:shadow-lg hover:shadow-orange-900/10 relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                    <BookOpen className="w-24 h-24 text-orange-500" />
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex justify-between items-start mb-4">
                      <span className="px-3 py-1 bg-orange-500/20 rounded-full text-xs font-medium text-orange-400 border border-orange-500/30">
                        {resource.category || 'Homework'}
                      </span>
                      {resource.estimated_time && (
                        <span className="flex items-center gap-1 text-stone-500 text-xs">
                          <Clock className="w-3 h-3" />
                          {resource.estimated_time}
                        </span>
                      )}
                    </div>
                    
                    <h3 className="text-xl font-medium text-white group-hover:text-orange-300 transition-colors mb-2">
                      {resource.title}
                    </h3>
                    
                    {resource.description && (
                      <p className="text-stone-400 text-sm line-clamp-2 mb-4">
                        {resource.description}
                      </p>
                    )}

                    <div className="flex items-center text-orange-400/80 text-sm font-medium group-hover:translate-x-1 transition-transform">
                      View Assignment <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 4. Stats & Progress (PRIORITY #4) */}
      <section className="py-8 px-6 border-b border-stone-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-4">
            {/* Overall Progress - with kind messaging */}
            <div className="bg-gradient-to-br from-stone-900 to-black border border-amber-500/30 rounded-lg p-4" data-testid="overall-progress">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                  <SpiralIcon className="w-4 h-4 text-black" />
                </div>
                <p className="text-stone-400 text-xs">Your Journey</p>
              </div>
              {overallProgress === 0 ? (
                <>
                  <p className="text-lg font-light text-amber-200 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Thank you for being here
                  </p>
                  <p className="text-stone-500 text-xs">Your presence matters</p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-light text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {Math.max(5, Math.round(overallProgress))}%
                  </p>
                  <div className="h-1.5 bg-stone-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-amber-500 to-amber-600 transition-all duration-1000"
                      style={{ width: `${Math.max(5, overallProgress)}%` }}
                    ></div>
                  </div>
                </>
              )}
            </div>

            {/* Exercises Completed */}
            <div className="bg-gradient-to-br from-stone-900 to-black border border-stone-700 rounded-lg p-4" data-testid="exercises-stat">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-stone-800 flex items-center justify-center">
                  <CrystalIcon className="w-4 h-4 text-amber-500" />
                </div>
                <p className="text-stone-400 text-xs">Exercises</p>
              </div>
              <p className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                {stats.completed}
              </p>
              <p className="text-stone-600 text-[10px] mt-1">of {totalExercises} completed</p>
            </div>

            {/* Reflections */}
            <div className="bg-gradient-to-br from-stone-900 to-black border border-stone-700 rounded-lg p-4" data-testid="reflections-stat">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-stone-800 flex items-center justify-center">
                  <FlameIcon className="w-4 h-4 text-amber-500" />
                </div>
                <p className="text-stone-400 text-xs">Reflections</p>
              </div>
              <p className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                {stats.total_responses || 0}
              </p>
              <p className="text-stone-600 text-[10px] mt-1">written responses</p>
            </div>

            {/* Journal Entries */}
            <div className="bg-gradient-to-br from-stone-900 to-black border border-stone-700 rounded-lg p-4" data-testid="journal-stat">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-stone-800 flex items-center justify-center">
                  <HeartAlchemyIcon className="w-4 h-4 text-amber-500" />
                </div>
                <p className="text-stone-400 text-xs">Journal Entries</p>
              </div>
              <p className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                {journal.length}
              </p>
              <p className="text-stone-600 text-[10px] mt-1">personal reflections</p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Main Content Tabs */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Tab Navigation */}
          <nav className="flex gap-1 mb-10 border-b border-stone-800 pb-1 overflow-x-auto">
            {[
              { id: 'summary', label: 'Journey', icon: Sparkles },
              { id: 'progress', label: 'Progress', icon: TrendingUp },
              { id: 'videos', label: 'Sessions', icon: PlayCircle },
              { id: 'journal', label: 'Journal', icon: PenLine },
              { id: 'resources', label: 'Resources', icon: FolderOpen },
              { id: 'lore', label: 'My LORE', icon: Star },
              { id: 'messages', label: 'Messages', icon: Mail },
              { id: 'feedback', label: 'Feedback', icon: Send },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 text-sm transition-all border-b-2 whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'text-amber-500 border-amber-500' 
                    : 'text-stone-500 border-transparent hover:text-stone-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Journey Summary Tab */}
          {activeTab === 'summary' && (
            <div className="space-y-10">
              {/* Loading State for Summary */}
              {dataLoading && !summary && responses.length === 0 && (
                <div className="flex items-center gap-3 text-amber-500 mb-4">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Loading your journey...</span>
                </div>
              )}
              
              <div className="bg-gradient-to-br from-stone-900 to-black border border-amber-500/20 rounded-lg p-8">
                <h2 className="text-3xl font-light text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Your NVC Journey
                </h2>
                {dataLoading && !summary ? (
                  <div className="space-y-3">
                    <Skeleton className="h-5 w-full bg-stone-800" />
                    <Skeleton className="h-5 w-3/4 bg-stone-800" />
                    <Skeleton className="h-5 w-1/2 bg-stone-800" />
                  </div>
                ) : (
                  <p className="text-xl text-stone-300 leading-relaxed">
                    {summary || 'Complete some exercises to see your personalized summary of growth and insights.'}
                  </p>
                )}
              </div>

              {/* Recent Reflections */}
              <div>
                <h3 className="text-stone-400 text-xs tracking-[0.2em] uppercase mb-6">Recent Reflections</h3>
                {dataLoading && responses.length === 0 ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="bg-stone-900/50 border border-stone-800 rounded-lg p-5 animate-pulse">
                        <Skeleton className="h-3 w-32 bg-stone-800 mb-3" />
                        <Skeleton className="h-4 w-full bg-stone-800 mb-2" />
                        <Skeleton className="h-4 w-3/4 bg-stone-800" />
                      </div>
                    ))}
                  </div>
                ) : responses.length === 0 ? (
                  <p className="text-stone-500">No reflections yet. Start working through the chapters!</p>
                ) : (
                  <div className="space-y-4">
                    {responses.slice(0, 5).map((r, i) => (
                      <div key={i} className="bg-stone-900/50 border border-stone-800 rounded-lg p-5">
                        <p className="text-amber-500 text-xs mb-2">Chapter {r.chapter_id} • Exercise {r.exercise_id}</p>
                        <p className="text-stone-300">{r.response_text.substring(0, 200)}{r.response_text.length > 200 ? '...' : ''}</p>
                        <p className="text-stone-600 text-xs mt-3">{new Date(r.updated_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Progress Tab */}
          {activeTab === 'progress' && (
            <div className="space-y-6">
              {chaptersData.chapters.map(chapter => {
                const chapterProgress = progress.filter(p => p.chapter_id === chapter.id && p.completed);
                const percent = chapter.exercises.length > 0 ? (chapterProgress.length / chapter.exercises.length) * 100 : 0;
                
                return (
                  <div key={chapter.id} className="bg-stone-900/50 border border-stone-800 rounded-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="text-amber-500 text-xs tracking-wide">CHAPTER {chapter.id}</p>
                        <h3 className="text-xl text-white font-light mt-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                          {chapter.title}
                        </h3>
                      </div>
                      <span className="text-2xl font-light text-amber-500">{Math.round(percent)}%</span>
                    </div>
                    <div className="h-2 bg-stone-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-amber-600 to-amber-500 transition-all duration-500"
                        style={{ width: `${percent}%` }}
                      ></div>
                    </div>
                    <p className="text-stone-500 text-xs mt-3">
                      {chapterProgress.length} of {chapter.exercises.length} exercises completed
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Videos/Sessions Tab */}
          {activeTab === 'videos' && (
            <div className="space-y-8" id="session-recordings" data-testid="session-recordings-section">
              <div>
                <h3 className="text-2xl text-white font-light mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Session Recordings
                </h3>
                <p className="text-stone-500 text-sm">Watch recordings from past sessions</p>
              </div>

              {/* Loading State */}
              {dataLoading && videos.length === 0 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 text-amber-500">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Loading session recordings...</span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    {[1, 2].map(i => (
                      <div key={i} className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-hidden animate-pulse">
                        <Skeleton className="aspect-video bg-stone-800" />
                        <div className="p-4 space-y-2">
                          <Skeleton className="h-5 w-3/4 bg-stone-800" />
                          <Skeleton className="h-4 w-1/2 bg-stone-800" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Latest/Featured Video */}
              {latestVideo && (
                <div className="bg-gradient-to-br from-stone-900 to-black border border-amber-500/30 rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-amber-500/20 flex items-center gap-2">
                    <PlayCircle className="w-5 h-5 text-amber-500" />
                    <span className="text-amber-400 font-medium">Latest Session</span>
                  </div>
                  <div className="aspect-video">
                    <iframe
                      width="100%"
                      height="100%"
                      src={`https://www.youtube.com/embed/${latestVideo.youtube_id}`}
                      title={latestVideo.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                  <div className="p-6">
                    <h4 className="text-xl text-white font-medium mb-2">{latestVideo.title}</h4>
                    {latestVideo.description && (
                      <p className="text-stone-400 text-sm mb-3">{latestVideo.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-stone-500 text-xs">
                      {latestVideo.session_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(latestVideo.session_date).toLocaleDateString()}
                        </span>
                      )}
                      {latestVideo.duration_minutes && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {latestVideo.duration_minutes} min
                        </span>
                      )}
                      {latestVideo.session_number && (
                        <span className="bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">
                          Session {latestVideo.session_number}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* All Videos Grid */}
              {videos.length > 0 ? (
                <div>
                  <h4 className="text-stone-400 text-xs tracking-[0.2em] uppercase mb-6">All Sessions</h4>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {videos.map(video => (
                      <div 
                        key={video._id} 
                        className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-hidden hover:border-amber-500/30 transition-colors group"
                      >
                        <div className="aspect-video relative">
                          <img 
                            src={`https://img.youtube.com/vi/${video.youtube_id}/mqdefault.jpg`}
                            alt={video.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <a 
                              href={video.youtube_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-amber-500 hover:bg-amber-400 text-black rounded-full p-3 transition-colors"
                            >
                              <Play className="w-6 h-6" />
                            </a>
                          </div>
                          {video.duration_minutes && (
                            <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                              {video.duration_minutes} min
                            </span>
                          )}
                        </div>
                        <div className="p-4">
                          <h5 className="text-white font-medium mb-1 line-clamp-2">{video.title}</h5>
                          <div className="flex items-center gap-2 text-stone-500 text-xs">
                            {video.session_date && (
                              <span>{new Date(video.session_date).toLocaleDateString()}</span>
                            )}
                            {video.session_number && (
                              <span className="bg-stone-800 px-2 py-0.5 rounded">Session {video.session_number}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : !dataLoading && (
                <div className="bg-stone-900/50 border border-stone-800 rounded-lg p-12 text-center">
                  <PlayCircle className="w-12 h-12 text-stone-600 mx-auto mb-4" />
                  <p className="text-stone-400">No session recordings available yet.</p>
                  <p className="text-stone-500 text-sm mt-2">Check back after your next live session!</p>
                </div>
              )}
            </div>
          )}

          {/* Journal Tab */}
          {activeTab === 'journal' && (
            <div className="space-y-8">
              {/* New Entry Form */}
              <div className="bg-gradient-to-br from-stone-900 to-black border border-amber-500/20 rounded-lg p-8">
                <h3 className="text-xl text-white font-light mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <PenLine className="w-5 h-5 inline mr-3 text-amber-500" />
                  New Journal Entry
                </h3>
                <div className="space-y-4">
                  <Input
                    value={newEntry.title}
                    onChange={(e) => setNewEntry({ ...newEntry, title: e.target.value })}
                    placeholder="Entry title..."
                    className="bg-stone-800/50 border-stone-700 text-white placeholder:text-stone-500 rounded-none focus:border-amber-500"
                  />
                  <Textarea
                    value={newEntry.content}
                    onChange={(e) => setNewEntry({ ...newEntry, content: e.target.value })}
                    placeholder="Write your thoughts, reflections, or insights..."
                    className="bg-stone-800/50 border-stone-700 text-white placeholder:text-stone-500 min-h-[150px] rounded-none focus:border-amber-500"
                  />
                  <Button 
                    onClick={addJournalEntry}
                    className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-black font-semibold rounded-none"
                  >
                    Save Entry
                  </Button>
                </div>
              </div>

              {/* Previous Entries */}
              <div>
                <h3 className="text-stone-400 text-xs tracking-[0.2em] uppercase mb-6">Previous Entries</h3>
                {journal.length === 0 ? (
                  <p className="text-stone-500">No journal entries yet. Start writing!</p>
                ) : (
                  <div className="space-y-4">
                    {journal.map((entry, i) => (
                      <div key={i} className="bg-stone-900/50 border border-stone-800 rounded-lg p-6">
                        <h4 className="text-lg text-white font-light mb-2">{entry.title}</h4>
                        <p className="text-stone-400 text-sm leading-relaxed">{entry.content}</p>
                        <p className="text-stone-600 text-xs mt-4 flex items-center gap-2">
                          <Calendar className="w-3 h-3" />
                          {new Date(entry.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Resources Tab */}
          {activeTab === 'resources' && (
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl text-white font-light mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Learning Materials
                </h3>
                <p className="text-stone-500 text-sm">Resources and homework to support your NVC journey</p>
              </div>

              {/* Loading State */}
              {dataLoading && resources.length === 0 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 text-amber-500">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Loading your learning materials...</span>
                  </div>
                  <ResourceSkeleton />
                </div>
              )}

              {/* Featured Resources & Required Homework */}
              {!dataLoading && resources.filter(r => r.is_featured).length > 0 && (
                <div className="mb-8">
                  <h4 className="text-lg text-amber-500 font-medium mb-4 flex items-center gap-2">
                    <Star className="w-4 h-4 fill-amber-500" />
                    Featured & Required
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {resources.filter(r => r.is_featured).map(resource => (
                      resource.material_type === 'homework' ? (
                        <button
                          key={resource._id}
                          onClick={() => setSelectedHomework(resource)}
                          className="group text-left bg-gradient-to-br from-amber-500/10 to-stone-900 border border-amber-500/30 rounded-lg p-6 hover:border-amber-500/50 transition-all"
                        >
                          <div className="flex items-start gap-4">
                            <div className="p-3 rounded-lg bg-orange-500/20 text-orange-400">
                              <BookOpen className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="px-2 py-0.5 bg-orange-500/20 rounded text-xs text-orange-400">Homework</span>
                                <span className="text-stone-500 text-xs">{resource.category}</span>
                              </div>
                              <h5 className="text-white font-medium group-hover:text-amber-400 transition-colors">
                                {resource.title}
                              </h5>
                              {resource.description && (
                                <p className="text-stone-400 text-sm mt-1 line-clamp-2">{resource.description}</p>
                              )}
                              <div className="flex items-center gap-3 mt-3">
                                {resource.estimated_time && (
                                  <span className="text-stone-500 text-xs flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {resource.estimated_time}
                                  </span>
                                )}
                                <span className="text-amber-500/70 text-xs">Click to view →</span>
                              </div>
                            </div>
                          </div>
                        </button>
                      ) : (
                        <a
                          key={resource._id}
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group bg-gradient-to-br from-amber-500/10 to-stone-900 border border-amber-500/30 rounded-lg p-6 hover:border-amber-500/50 transition-all"
                        >
                          <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-lg ${
                              resource.resource_type === 'video' ? 'bg-purple-500/20 text-purple-400' :
                              resource.resource_type === 'document' ? 'bg-blue-500/20 text-blue-400' :
                              resource.resource_type === 'file' ? 'bg-green-500/20 text-green-400' :
                              'bg-amber-500/20 text-amber-400'
                            }`}>
                              {resource.resource_type === 'video' ? <Video className="w-5 h-5" /> :
                               resource.resource_type === 'document' ? <FileText className="w-5 h-5" /> :
                               resource.resource_type === 'file' ? <FolderOpen className="w-5 h-5" /> :
                               <Link2 className="w-5 h-5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-white font-medium group-hover:text-amber-400 transition-colors">
                                {resource.title}
                              </h5>
                              {resource.description && (
                                <p className="text-stone-400 text-sm mt-1 line-clamp-2">{resource.description}</p>
                              )}
                              <div className="flex items-center gap-3 mt-3">
                                <span className="px-2 py-0.5 bg-stone-800 rounded text-xs text-stone-400">{resource.category}</span>
                                <span className="inline-flex items-center gap-1 text-amber-500/70 text-xs">
                                  <ExternalLink className="w-3 h-3" />
                                  Open
                                </span>
                              </div>
                            </div>
                          </div>
                        </a>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* Homework Assignments Section */}
              {resources.filter(r => r.material_type === 'homework').length > 0 && (
                <div className="mb-8">
                  <h4 className="text-lg text-white font-medium mb-4 pb-2 border-b border-stone-800 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-orange-400" />
                    Homework Assignments
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {resources.filter(r => r.material_type === 'homework' && !r.is_featured).map(resource => (
                      <button
                        key={resource._id}
                        onClick={() => setSelectedHomework(resource)}
                        className="group text-left bg-stone-900/50 border border-stone-800 rounded-lg p-4 hover:border-orange-500/50 hover:bg-stone-900 transition-all"
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-orange-500/20 text-orange-400 flex-shrink-0">
                            <BookOpen className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-orange-400/70 text-xs">{resource.category}</span>
                            <h5 className="text-stone-200 text-sm font-medium group-hover:text-white truncate">
                              {resource.title}
                            </h5>
                            {resource.estimated_time && (
                              <p className="text-stone-500 text-xs mt-1">{resource.estimated_time}</p>
                            )}
                          </div>
                          <ChevronRight className="w-4 h-4 text-stone-600 group-hover:text-orange-400 flex-shrink-0" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Resources by Type */}
              {resources.length > 0 ? (
                <div className="space-y-10">
                  {/* Links */}
                  {resources.filter(r => r.resource_type === 'link' && !r.is_featured).length > 0 && (
                    <div>
                      <h4 className="text-lg text-white font-medium mb-4 pb-2 border-b border-stone-800 flex items-center gap-2">
                        <Link2 className="w-5 h-5 text-teal-400" />
                        Links & Websites
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {resources.filter(r => r.resource_type === 'link' && !r.is_featured).map(resource => (
                          <a key={resource._id} href={resource.url} target="_blank" rel="noopener noreferrer"
                            className="group flex items-center gap-3 bg-stone-900/50 border border-stone-800 rounded-lg p-4 hover:border-teal-500/50 hover:bg-stone-900 transition-all">
                            <div className="p-2 rounded-lg bg-teal-500/20 text-teal-400 flex-shrink-0">
                              <Link2 className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-stone-200 text-sm font-medium truncate group-hover:text-white">{resource.title}</h5>
                              {resource.description && <p className="text-stone-500 text-xs truncate">{resource.description}</p>}
                            </div>
                            <ExternalLink className="w-4 h-4 text-stone-600 group-hover:text-teal-400 flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Documents */}
                  {resources.filter(r => r.resource_type === 'document' && !r.is_featured).length > 0 && (
                    <div>
                      <h4 className="text-lg text-white font-medium mb-4 pb-2 border-b border-stone-800 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-400" />
                        Documents & PDFs
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {resources.filter(r => r.resource_type === 'document' && !r.is_featured).map(resource => (
                          <a key={resource._id} href={resource.url} target="_blank" rel="noopener noreferrer"
                            className="group flex items-center gap-3 bg-stone-900/50 border border-stone-800 rounded-lg p-4 hover:border-blue-500/50 hover:bg-stone-900 transition-all">
                            <div className="p-2 rounded-lg bg-blue-500/20 text-blue-400 flex-shrink-0">
                              <FileText className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-stone-200 text-sm font-medium truncate group-hover:text-white">{resource.title}</h5>
                              {resource.description && <p className="text-stone-500 text-xs truncate">{resource.description}</p>}
                            </div>
                            <Download className="w-4 h-4 text-stone-600 group-hover:text-blue-400 flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Videos */}
                  {resources.filter(r => r.resource_type === 'video' && !r.is_featured).length > 0 && (
                    <div>
                      <h4 className="text-lg text-white font-medium mb-4 pb-2 border-b border-stone-800 flex items-center gap-2">
                        <Video className="w-5 h-5 text-purple-400" />
                        Videos
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {resources.filter(r => r.resource_type === 'video' && !r.is_featured).map(resource => (
                          <a key={resource._id} href={resource.url} target="_blank" rel="noopener noreferrer"
                            className="group flex items-center gap-3 bg-stone-900/50 border border-stone-800 rounded-lg p-4 hover:border-purple-500/50 hover:bg-stone-900 transition-all">
                            <div className="p-2 rounded-lg bg-purple-500/20 text-purple-400 flex-shrink-0">
                              <Video className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-stone-200 text-sm font-medium truncate group-hover:text-white">{resource.title}</h5>
                              {resource.description && <p className="text-stone-500 text-xs truncate">{resource.description}</p>}
                            </div>
                            <ExternalLink className="w-4 h-4 text-stone-600 group-hover:text-purple-400 flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Files */}
                  {resources.filter(r => r.resource_type === 'file' && !r.is_featured).length > 0 && (
                    <div>
                      <h4 className="text-lg text-white font-medium mb-4 pb-2 border-b border-stone-800 flex items-center gap-2">
                        <FolderOpen className="w-5 h-5 text-green-400" />
                        Files & Downloads
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {resources.filter(r => r.resource_type === 'file' && !r.is_featured).map(resource => (
                          <a key={resource._id} href={resource.url} target="_blank" rel="noopener noreferrer"
                            className="group flex items-center gap-3 bg-stone-900/50 border border-stone-800 rounded-lg p-4 hover:border-green-500/50 hover:bg-stone-900 transition-all">
                            <div className="p-2 rounded-lg bg-green-500/20 text-green-400 flex-shrink-0">
                              <FolderOpen className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-stone-200 text-sm font-medium truncate group-hover:text-white">{resource.title}</h5>
                              {resource.description && <p className="text-stone-500 text-xs truncate">{resource.description}</p>}
                            </div>
                            <Download className="w-4 h-4 text-stone-600 group-hover:text-green-400 flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : !dataLoading && (
                <div className="bg-stone-900/50 border border-stone-800 rounded-lg p-12 text-center">
                  <FolderOpen className="w-12 h-12 text-stone-600 mx-auto mb-4" />
                  <p className="text-stone-400">No resources available yet.</p>
                  <p className="text-stone-500 text-sm mt-2">Your instructor will upload study materials and helpful links soon.</p>
                  <p className="text-stone-600 text-xs mt-4">Check the Announcements section for class updates.</p>
                </div>
              )}
            </div>
          )}

          {/* My LORE Tab - Personal Library & Blog Submission */}
          {activeTab === 'lore' && (
            <div className="space-y-10">
              {/* Personal LORE Library */}
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl text-white font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Your Personal LORE Library
                    </h3>
                    <p className="text-stone-500 text-sm mt-1">Your private collection of wisdom and growth moments</p>
                  </div>
                  <a href="/lore">
                    <Button className="bg-transparent border border-amber-500/50 text-amber-500 hover:bg-amber-500/20 rounded-none">
                      View Public LORE
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Journal entries as LORE */}
                  {journal.slice(0, 4).map((entry, i) => (
                    <div key={i} className="bg-stone-900/50 border border-stone-800 rounded-lg p-6 hover:border-amber-500/30 transition-colors">
                      <p className="text-amber-500 text-xs tracking-wide mb-2">PERSONAL REFLECTION</p>
                      <h4 className="text-lg text-white font-light mb-3">{entry.title}</h4>
                      <p className="text-stone-400 text-sm line-clamp-3">{entry.content}</p>
                      <p className="text-stone-600 text-xs mt-4">{new Date(entry.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Story to LORE */}
              <div className="bg-gradient-to-br from-amber-900/20 to-stone-900 border border-amber-500/30 rounded-lg p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Star className="w-6 h-6 text-amber-500" />
                  <div>
                    <h3 className="text-xl text-white font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Share Your Story
                    </h3>
                    <p className="text-amber-200/60 text-sm">Submit a story for the public LORE library</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <Input
                    value={blogDraft.title}
                    onChange={(e) => setBlogDraft({ ...blogDraft, title: e.target.value })}
                    placeholder="Story title..."
                    className="bg-stone-800/50 border-stone-700 text-white placeholder:text-stone-500 rounded-none focus:border-amber-500"
                  />
                  <Textarea
                    value={blogDraft.excerpt}
                    onChange={(e) => setBlogDraft({ ...blogDraft, excerpt: e.target.value })}
                    placeholder="Brief description (shown in preview)..."
                    className="bg-stone-800/50 border-stone-700 text-white placeholder:text-stone-500 h-20 rounded-none focus:border-amber-500"
                  />
                  <Textarea
                    value={blogDraft.content}
                    onChange={(e) => setBlogDraft({ ...blogDraft, content: e.target.value })}
                    placeholder="Your story... Share your journey, insights, or transformation through NVC."
                    className="bg-stone-800/50 border-stone-700 text-white placeholder:text-stone-500 min-h-[200px] rounded-none focus:border-amber-500"
                  />
                  <div className="flex justify-between items-center pt-4">
                    <p className="text-stone-500 text-xs">Stories are reviewed by admin before publishing</p>
                    <Button 
                      onClick={submitBlogPost}
                      className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-black font-semibold rounded-none"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Submit for Review
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div className="bg-stone-900/50 border border-stone-800 rounded-lg p-6">
              <MessagingCenter user={user} />
            </div>
          )}

          {/* Feedback Tab */}
          {activeTab === 'feedback' && (
            <div className="bg-stone-900/50 border border-stone-800 rounded-lg p-6">
              <FeedbackForm userId={user?.id} />
            </div>
          )}
        </div>
      </section>

      {/* Quick Actions Footer */}
      <section className="py-10 px-6 border-t border-stone-800 bg-gradient-to-t from-stone-900/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={downloadPDF}
              className="bg-transparent border border-stone-700 text-stone-400 hover:text-white hover:border-amber-500 rounded-none"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <a href="/ofnr-trainer">
              <Button className="bg-transparent border border-stone-700 text-stone-400 hover:text-white hover:border-amber-500 rounded-none">
                OFNR Trainer
              </Button>
            </a>
            <Button 
              onClick={launchElchmE}
              disabled={launchingElchmE}
              className="bg-transparent border border-stone-700 text-stone-400 hover:text-white hover:border-amber-500 rounded-none"
            >
              {launchingElchmE ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              ElchmE Board
            </Button>
          </div>
        </div>
      </section>

      {/* Premium Footer */}
      <PremiumFooter
        variant="dark"
        bottomDock={
          <DashboardBottomDock
            onRequestScholarship={() => setShowScholarshipDialog(true)}
          />
        }
      />

      {/* Scholarship Dialog */}
      <ScholarshipRequestDialog 
        open={showScholarshipDialog} 
        onOpenChange={setShowScholarshipDialog}
        user={user}
      />

      {/* Homework Content Dialog */}
      <Dialog open={!!selectedHomework} onOpenChange={() => setSelectedHomework(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-stone-900 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-light flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              <div className="p-2 rounded-lg bg-orange-500/20 text-orange-400">
                <BookOpen className="w-5 h-5" />
              </div>
              {selectedHomework?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedHomework && (
            <div className="space-y-6 mt-4">
              {/* Meta info */}
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full">
                  {selectedHomework.category}
                </span>
                {selectedHomework.estimated_time && (
                  <span className="flex items-center gap-1 text-stone-400">
                    <Calendar className="w-4 h-4" />
                    {selectedHomework.estimated_time}
                  </span>
                )}
                {selectedHomework.page_reference && (
                  <span className="text-stone-500">
                    Reference: {selectedHomework.page_reference}
                  </span>
                )}
              </div>

              {/* Description */}
              {selectedHomework.description && (
                <div className="bg-stone-800/50 border border-stone-700 rounded-lg p-4">
                  <p className="text-stone-300">{selectedHomework.description}</p>
                </div>
              )}

              {/* Instructions */}
              {selectedHomework.instructions && (
                <div>
                  <h4 className="text-amber-500 font-medium mb-2 text-sm uppercase tracking-wide">Instructions</h4>
                  <p className="text-stone-300 leading-relaxed">{selectedHomework.instructions}</p>
                </div>
              )}

              {/* Main Content */}
              {selectedHomework.content && (
                <div>
                  <h4 className="text-amber-500 font-medium mb-3 text-sm uppercase tracking-wide">Content</h4>
                  <div className="bg-stone-800/30 border border-stone-700/50 rounded-lg p-6">
                    <div className="prose prose-invert prose-stone max-w-none">
                      <div className="text-stone-200 leading-relaxed whitespace-pre-wrap">
                        {selectedHomework.content}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Close button */}
              <div className="flex justify-end pt-4 border-t border-stone-700">
                <Button 
                  onClick={() => setSelectedHomework(null)}
                  className="bg-amber-600 hover:bg-amber-500 text-black font-medium rounded-none"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* User Messaging Dialog */}
      <UserMessaging 
        isOpen={showMessaging} 
        onClose={() => {
          setShowMessaging(false);
          setInitialConversationId(null);
        }}
        initialConversationId={initialConversationId}
      />
    </div>
  );
}

export default Dashboard;
